import { forwardRef, type ElementRef } from "react";

import { cn } from "../../lib";
import { Box } from "../../primitives";
import {
  dialogDividerColor,
  dialogFooterAfterBody,
  dialogSectionGap,
  dialogSectionPadding,
  useDialogContext,
} from "./lib";
import type { DialogFooterProps } from "./types";

/**
 * Dialog.Footer
 *
 * Sticky region below the body, 8px gap (12 from `md`). Below a Body that
 * fits, its top padding is 0 on mobile, leaving the Body's 16 as the gap above
 * the buttons (48 on desktop, 24 + 24). `layout="row"` (the
 * default) puts the buttons side by side at the end, hugging their labels;
 * `layout="stacked"` gives each a full-width row. Carries a `border-t` separator while the
 * Body's content scrolls, unless `Dialog.Root` is given `divider={false}`.
 */
export const DialogFooter = forwardRef<ElementRef<typeof Box>, DialogFooterProps>(
  ({ children, layout = "row", className }, ref) => {
    const { divider, bodyOverflowing, hasBody } = useDialogContext();
    // One 16 gap above the buttons on mobile: below a Body that fits, the
    // Body's bottom padding is that gap (as in BottomSheet)
    const followsBody = hasBody && !bodyOverflowing;

    return (
      <Box
        ref={ref}
        as="footer"
        className={cn(
          dialogSectionGap,
          "flex shrink-0",
          layout === "row" ? "flex-row items-center justify-end" : "flex-col items-stretch",
          divider && bodyOverflowing && cn("border-t", dialogDividerColor),
          dialogSectionPadding,
          followsBody && dialogFooterAfterBody,
          className,
        )}
      >
        {children}
      </Box>
    );
  },
);

DialogFooter.displayName = "Dialog.Footer";
